import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import DeactivateConfirmDialog from "./DeactivateConfirmDialog";
import * as XLSX from "xlsx";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import { getApiErrorMessage } from "../../../utils/apiError";
import { colors } from "../../../theme/colors";

interface ManagersListProps {
  refreshKey?: number;
  onEditManager?: (user: UserRow) => void;
}

interface UserRow {
  id: string;
  userId: string;
  name: string;
  stationType: string;
  stationName: string;
  email: string;
  phone: string;
  status: "Active" | "Inactive";
}

interface ManagersApiResponse {
  count?: number;
  total?: number;
  total_count?: number;
  results?: unknown[];
  items?: unknown[];
  data?: unknown[];
  managers?: unknown[];
}

const PAGE_SIZE = 20;

const getManagersArray = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const response = payload as ManagersApiResponse;
  return (
    response.results ||
    response.items ||
    response.data ||
    response.managers ||
    []
  );
};

const getManagersTotal = (payload: unknown, fallbackLength: number) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return fallbackLength;
  }

  const response = payload as ManagersApiResponse;
  return (
    response.count || response.total || response.total_count || fallbackLength
  );
};

const normalizeStationType = (value?: string) => {
  if (!value) {
    return "Filling";
  }

  if (value === "FUEL_STATION" || value === "Fuel Station") {
    return "Filling";
  }

  if (
    value === "TESTING_CENTRE" ||
    value === "TESTING_CENTER" ||
    value === "Testing Centre" ||
    value === "Testing Center"
  ) {
    return "Testing";
  }

  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (match) => match.toUpperCase());
};

const normalizeStatus = (value: unknown): "Active" | "Inactive" => {
  if (typeof value === "boolean") {
    return value ? "Active" : "Inactive";
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "active" ? "Active" : "Inactive";
  }

  return "Inactive";
};

const normalizeManager = (item: any, index: number): UserRow => ({
  id: item?.id || `MGR-${index + 1}`,
  userId: item?.user_id || item?.id || `MGR-${index + 1}`,
  name:
    item?.name || item?.full_name || item?.manager_name || "Unknown Manager",
  stationType: normalizeStationType(item?.station_type || item?.stationType),
  stationName:
    item?.station_name ||
    item?.assigned_station_name ||
    item?.station?.name ||
    item?.station ||
    "--",
  email: item?.email || "--",
  phone: item?.phone || item?.phone_number || item?.mobile || "--",
  status: normalizeStatus(item?.is_active ?? item?.status),
});

const getManagerFilterParams = ({
  debouncedName,
  debouncedEmail,
  debouncedStation,
  userStatus,
}: {
  debouncedName: string;
  debouncedEmail: string;
  debouncedStation: string;
  userStatus: string;
}) => ({
  search:
    [debouncedName, debouncedEmail].filter(Boolean).join(" ").trim() ||
    undefined,
  station_type: undefined,
  station_name: debouncedStation || undefined,
  is_active: userStatus === "ALL" ? undefined : userStatus === "Active",
});

const getManagerExportData = (users: UserRow[]) =>
  users.map((user) => ({
    ID: user.userId,
    Name: user.name,
    "Station Type": user.stationType,
    "Station Name": user.stationName,
    Email: user.email,
    Phone: user.phone,
    Status: user.status,
  }));

const getStatusColor = (status: string) => {
  return status === "Active" ? "#2E7D32" : "#E53935";
};

const getStationTypeColor = (type: string) => {
  return type === "Filling" ? "#2E7D32" : "#42A5F5";
};

const filterButtonSx = (selected: boolean) => ({
  minWidth: "fit-content",
  minHeight: 26,
  px: 1,
  py: 1.25,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: selected ? "#D9D9D9" : "transparent",
  bgcolor: selected ? "#F3F4F6" : "transparent",
  color: selected ? colors.black : "rgba(0, 0, 0, 0.54)",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  fontFamily: "'Inter', sans-serif",
  "&:hover": {
    borderColor: selected ? "#D9D9D9" : "transparent",
    bgcolor: selected ? "#F3F4F6" : "transparent",
    boxShadow: "none",
  },
});

const filterInputSx = {
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "4px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#D9D9D9",
    },
    "&:hover fieldset": {
      borderColor: "#D0D5DD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#B8C0CC",
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "13px",
    color: colors.black,
    py: 0.95,
    fontFamily: "'Inter', sans-serif",
  },
};

const getStationTypeChipSx = (stationType: string) => ({
  bgcolor:
    stationType === "Filling"
      ? "rgba(76, 175, 80, 0.14)"
      : "rgba(66, 165, 245, 0.18)",
  color: getStationTypeColor(stationType),
  fontWeight: 600,
  fontSize: "9px",
  borderRadius: "999px",
  height: 20,
  fontFamily: "'Inter', sans-serif",
  "& .MuiChip-label": {
    px: 0.95,
  },
});

const getStatusChipSx = (status: UserRow["status"]) => ({
  bgcolor:
    status === "Active" ? "rgba(76, 175, 80, 0.14)" : "rgba(244, 67, 54, 0.12)",
  color: getStatusColor(status),
  fontWeight: 600,
  fontSize: "9px",
  borderRadius: "999px",
  height: 20,
  fontFamily: "'Inter', sans-serif",
  "& .MuiChip-label": {
    px: 0.95,
  },
});

const statusFilters = [
  { label: "ALL", value: "ALL" },
  { label: "ACTIVE", value: "Active" },
  { label: "INACTIVE", value: "Inactive" },
];

const ManagersList: React.FC<ManagersListProps> = ({
  refreshKey = 0,
  onEditManager,
}) => {
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");
  const [userStatus, setUserStatus] = useState<string>("ALL");
  const [stationName, setStationName] = useState("");
  const [debouncedStation, setDebouncedStation] = useState("");
  const [page, setPage] = useState(1);
  const [usersData, setUsersData] = useState<UserRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<UserRow | null>(
    null,
  );
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const activeRequestIdRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedName(nameFilter.trim());
      setDebouncedEmail(emailFilter.trim());
      setDebouncedStation(stationName.trim());
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [nameFilter, emailFilter, stationName]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = activeRequestIdRef.current + 1;

    activeRequestIdRef.current = requestId;

    const fetchManagers = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await api.get(endpoints.users.createManager, {
          params: {
            ...getManagerFilterParams({
              debouncedName,
              debouncedEmail,
              debouncedStation,
              userStatus,
            }),
            page,
            page_size: PAGE_SIZE,
          },
          signal: controller.signal,
        });

        const managers = getManagersArray(response.data).map((item, index) =>
          normalizeManager(item, index),
        );

        setUsersData(managers);
        setTotalCount(getManagersTotal(response.data, managers.length));
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return;
        }

        const message = getApiErrorMessage(
          err,
          "Failed to load managers. Please try again.",
        );

        setErrorMessage(message);
        setUsersData([]);
        setTotalCount(0);
      } finally {
        if (activeRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    fetchManagers();

    return () => {
      controller.abort();
    };
  }, [
    debouncedName,
    debouncedEmail,
    debouncedStation,
    userStatus,
    page,
    refreshKey,
  ]);

  const handleToggleStatus = async () => {
    if (!confirmDeactivate) return;
    const newIsActive = confirmDeactivate.status !== "Active";
    setIsStatusUpdating(true);
    try {
      await api.patch(
        endpoints.users.updateManagerStatus(confirmDeactivate.id),
        {
          is_active: newIsActive,
        },
      );
      setUsersData((prev) =>
        prev.map((u) =>
          u.id === confirmDeactivate.id
            ? { ...u, status: newIsActive ? "Active" : "Inactive" }
            : u,
        ),
      );
      setConfirmDeactivate(null);
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        "Failed to update status. Please try again.",
      );
      setErrorMessage(message);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleClearFilters = () => {
    setUserStatus("ALL");
    setNameFilter("");
    setEmailFilter("");
    setStationName("");
    setPage(1);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);

    try {
      const response = await api.get(endpoints.users.exportManagers, {
        params: getManagerFilterParams({
          debouncedName,
          debouncedEmail,
          debouncedStation,
          userStatus,
        }),
      });

      const managers = getManagersArray(response.data).map((item, index) =>
        normalizeManager(item, index),
      );
      const exportData = getManagerExportData(managers);

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      worksheet["!cols"] = [
        { wch: 16 },
        { wch: 22 },
        { wch: 16 },
        { wch: 28 },
        { wch: 30 },
        { wch: 18 },
        { wch: 12 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Managers");

      const summarySheet = XLSX.utils.aoa_to_sheet([
        ["Report Generated", new Date().toLocaleString()],
        ["Exported Managers", managers.length],
        ["Total Records", getManagersTotal(response.data, managers.length)],
        [
          "Active Managers",
          managers.filter((user) => user.status === "Active").length,
        ],
        [
          "Inactive Managers",
          managers.filter((user) => user.status === "Inactive").length,
        ],
      ]);

      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      XLSX.writeFile(
        workbook,
        `Manager_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        "Failed to export managers. Please try again.",
      );
      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#F5F5F5", minHeight: "100vh", py: 3 }}>
      <Container maxWidth="xl">
        <Paper
          sx={{
            mb: 3,
            borderRadius: "8px",
            border: "1px solid #D8D8D8",
            boxShadow: "0 2px 4px rgba(15, 23, 42, 0.12)",
            overflow: "hidden",
            bgcolor: "#FFFFFF",
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                color: colors.black,
                mb: 3,
              }}
            >
              Filter & Search
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: { xs: 3, md: 2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0.8,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: colors.black,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Status
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.65,
                    flexWrap: "wrap",
                  }}
                >
                  {statusFilters.map((option) => (
                    <Button
                      key={option.value}
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        setUserStatus(option.value);
                        setPage(1);
                      }}
                      sx={filterButtonSx(userStatus === option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(3, minmax(146px, 148px))",
                  },
                  gap: 1.1,
                }}
              >
                <TextField
                  placeholder="Enter Name"
                  autoComplete="off"
                  value={nameFilter}
                  onChange={(e) => {
                    setNameFilter(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  sx={filterInputSx}
                />
                <TextField
                  placeholder="Enter Email"
                  autoComplete="off"
                  value={emailFilter}
                  onChange={(e) => {
                    setEmailFilter(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  sx={filterInputSx}
                />
                <TextField
                  placeholder="Enter Station"
                  autoComplete="off"
                  value={stationName}
                  onChange={(e) => {
                    setStationName(e.target.value);
                    setPage(1);
                  }}
                  size="small"
                  sx={filterInputSx}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #D8D8D8",
            boxShadow: "0 2px 4px rgba(15, 23, 42, 0.12)",
          }}
        >
          <Box
            sx={{
              px: 2.2,
              py: 1.75,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #ECECEC",
              bgcolor: "white",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#2B2B2B",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              User List
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportToExcel}
              disabled={isExporting}
              sx={{
                textTransform: "none",
                borderColor: "#74C67A",
                color: "#4B9F51",
                borderRadius: "4px",
                minHeight: 30,
                px: 1.4,
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                "&:hover": {
                  borderColor: "#4B9F51",
                  bgcolor: "#F0FDF4",
                  color: "#3F8F46",
                },
              }}
            >
              {isExporting ? "Exporting..." : "Export Data"}
            </Button>
          </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F7F7F7" }}>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      borderBottom: "1px solid #E6E6E6",
                      py: 1.7,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      borderBottom: "1px solid #E6E6E6",
                      py: 1.7,
                      whiteSpace: "nowrap",
                    }}
                  >
                    User ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      borderBottom: "1px solid #E6E6E6",
                      py: 1.7,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Station type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      py: 1.7,
                      borderBottom: "1px solid #E6E6E6",
                    }}
                  >
                    Station
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      py: 1.7,
                      borderBottom: "1px solid #E6E6E6",
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      py: 1.7,
                      borderBottom: "1px solid #E6E6E6",
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      py: 1.7,
                      borderBottom: "1px solid #E6E6E6",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#2F2F2F",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "11px",
                      py: 1.7,
                      borderBottom: "1px solid #E6E6E6",
                    }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box
                        sx={{
                          py: 7,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  usersData.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #EDEDED",
                          py: 1.45,
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: "#2E7D32",
                              fontSize: "11px",
                              fontWeight: 700,
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            {(user.name || "U").charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#2F2F2F",
                              fontSize: "12px",
                              fontFamily: "'Inter', sans-serif",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#7B7B7B",
                            fontSize: "12px",
                            fontFamily: "'Inter', sans-serif",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.stationType}
                          size="small"
                          sx={getStationTypeChipSx(user.stationType)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#7B7B7B",
                            fontSize: "12px",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {user.stationName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#7B7B7B",
                            fontSize: "12px",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#7B7B7B",
                            fontSize: "12px",
                            fontFamily: "'Inter', sans-serif",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status}
                          size="small"
                          sx={getStatusChipSx(user.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.35,
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{ color: "#1E88E5", p: 0.45 }}
                            title="Edit Manager"
                            onClick={() => onEditManager?.(user)}
                          >
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                          {user.status === "Active" ? (
                            <IconButton
                              size="small"
                              sx={{ color: "#D32F2F", p: 0.45 }}
                              title="Deactivate Manager"
                              onClick={() => setConfirmDeactivate(user)}
                            >
                              <BlockIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              sx={{ color: "#2E7D32", p: 0.45 }}
                              title="Activate Manager"
                              onClick={() => setConfirmDeactivate(user)}
                            >
                              <CheckCircleIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!isLoading && usersData.length > 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                borderTop: "1px solid #ECECEC",
              }}
            >
              <Typography sx={{ color: "#666666", fontSize: "14px" }}>
                Showing {usersData.length} of {totalCount} users
              </Typography>
              <Pagination
                count={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}

          {!isLoading && usersData.length === 0 && (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" sx={{ color: "#64748B" }}>
                No managers found matching your criteria
              </Typography>
              <Button
                onClick={handleClearFilters}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </Paper>

        <DeactivateConfirmDialog
          open={!!confirmDeactivate}
          isActive={confirmDeactivate?.status === "Active"}
          isSubmitting={isStatusUpdating}
          onClose={() => !isStatusUpdating && setConfirmDeactivate(null)}
          onConfirm={handleToggleStatus}
        />
      </Container>
    </Box>
  );
};

export default ManagersList;
